/**
 * WHICH TOLERANCE GOVERNS ONE ANSWER.
 *
 * The five blocks that grade a number against a tolerance (Numerical, Slider, Compass, Tilt,
 * Rotation) each accept several answers under OR logic, and each answer carries its own band:
 * `toleranceValue` when symmetric, `toleranceBelow`/`toleranceAbove` when not.
 *
 * ## Why these functions exist
 *
 * The two SETTINGS that say how to read those numbers — symmetric/asymmetric, absolute/percentage
 * — used to live on the block, one each for every answer. That made two questions unauthorable:
 *
 *   - accepted answers spanning zero, where a percentage band collapses on the small one and an
 *     absolute band is meaninglessly tight on the large one;
 *   - an answer sitting against a natural floor, wanting `−0 +5` while its siblings stay `±2`.
 *
 * Neither is incoherent: OR-logic answers are independent acceptance windows, so there is no
 * cross-answer invariant for a mixed setting to violate. They are now per answer.
 *
 * ## Why they take a `legacy` argument
 *
 * Blocks authored before the move have the settings on the block and nothing on their answers.
 * Rather than rewrite stored documents, every read goes through here and falls back. The editor
 * always writes the per-answer field, so a block becomes self-describing the first time it is
 * touched, and the block-level field is read by nothing else.
 *
 * `symmetric` is the final fallback because it is the shape an answer with only `toleranceValue` set
 * is already in — reading such an answer as asymmetric would silently widen its band to
 * `toleranceBelow`/`toleranceAbove`, which on a fresh answer are 0, collapsing it to an exact match.
 */
export function answerSymmetry(answer, legacy) {
    return answer.toleranceSymmetry ?? legacy?.toleranceSymmetry ?? 'symmetric';
}
/**
 * The two half-widths of this answer's band, before any percentage is applied.
 *
 * This replaces the `toleranceSymmetry === 'asymmetric' ? a.toleranceBelow : a.toleranceValue` pair that was
 * written out at fourteen sites across the five blocks — the kind of duplication where one site
 * gets the ternary backwards and nothing catches it, because both branches are plausible numbers.
 */
export function answerHalfWidths(answer, legacy) {
    return answerSymmetry(answer, legacy) === 'asymmetric'
        ? { below: answer.toleranceBelow, above: answer.toleranceAbove }
        : { below: answer.toleranceValue, above: answer.toleranceValue };
}
/**
 * Absolute or percentage, for the two blocks that have the choice.
 *
 * Generic over the mode type because Numerical and Slider name theirs separately, and untyped
 * here so the three angle blocks — which have no percentage mode, there being no privileged zero
 * on a bearing — cannot accidentally acquire one by calling this.
 */
export function answerToleranceMode(answer, legacy, fallback) {
    return answer.toleranceMode ?? legacy?.toleranceMode ?? fallback;
}
/**
 * Whether the answers disagree about how their tolerances should be read.
 *
 * Authoring this is allowed — see the module note — but it is worth saying out loud, because a
 * column of bands that mean different things is the one real cost of the per-answer move. The
 * editor raises it as a warning, never an error: a missing capability is costed, not forbidden.
 *
 * One answer can never disagree with itself, and a block whose answers all omit the fields (an
 * untouched legacy block) is uniform by definition.
 */
export function tolerancesDiverge(answers, legacy) {
    if (answers.length < 2)
        return false;
    const seen = new Set(answers.map(a => `${answerSymmetry(a, legacy)}|${a.toleranceMode ?? legacy?.toleranceMode ?? ''}`));
    return seen.size > 1;
}
//# sourceMappingURL=answer-tolerance.js.map